import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Text, Label, Input, Button, Skeleton } from '@mercai/clever'
import { useQueryClient } from '@tanstack/react-query'

import { isSuperUser } from '@/helpers'
import { useAuth } from '@/providers'

import {
  useAIConversationDetail,
  useAICreateWhatsappMessage,
  useAIForceFinishedConversation,
  QUERIES_ADMIN,
} from '@/services'

import { RegenerateConversationProps } from './regenerate-conversation.type'

import styles from './regenerate-conversation.module.css'

export const RegenerateConversation = ({
  isOpen,
  onHandleOpen,
  uuid,
}: RegenerateConversationProps) => {
  const { t } = useTranslation('screenAiCenter')
  const queryClient = useQueryClient()
  const { whoAmI } = useAuth()

  const [selectedAgent, setSelectedAgent] = useState('')

  const [responseRegenerateConversation, setResponseRegenerateConversation] =
    useState('')

  const {
    mutateAsync: createWhatsappMessage,
    isPending: isPendingCreateWhatsappMessage,
  } = useAICreateWhatsappMessage()

  const {
    mutateAsync: regenerateConversation,
    isPending: isPendingRegenerateConversation,
  } = useAIForceFinishedConversation()

  const { data: conversationDetail, isLoading: isLoadingConversationDetail } =
    useAIConversationDetail(uuid)

  const onRequestCreateWhatsappMessage = async () => {
    const response = await createWhatsappMessage({
      content: responseRegenerateConversation,
      conversationUuid: uuid,
    })

    setResponseRegenerateConversation('')

    if (response?.success) {
      queryClient.refetchQueries({
        queryKey: [QUERIES_ADMIN.AI_CONVERSATION.module],
      })
    }
  }

  const onRequestRegenerateConversation = async () => {
    const response = await regenerateConversation({
      uuid,
      agent: selectedAgent,
    })

    setResponseRegenerateConversation((response?.data || '') as string)

    if (response?.success) {
      queryClient.refetchQueries({
        queryKey: [QUERIES_ADMIN.AI_CONVERSATION.module],
      })
    }
  }

  const activateAgents = useMemo(() => {
    if (isLoadingConversationDetail) {
      return (
        <div className={styles['agents-container']}>
          <Skeleton height="100px" width="5rem" className={styles.skeleton} />
        </div>
      )
    }

    const agents = conversationDetail?.data?.messages?.reduce(
      (acc, message) => {
        if (message.context && !acc.includes(message.context)) {
          acc.push(message.context)
        }
        return acc
      },
      [] as string[],
    )

    return (
      <div className={styles['agents-container']}>
        {agents?.map((agent) => (
          <Button
            model="pill"
            isActive={selectedAgent === agent}
            key={agent}
            variant="secondary"
            onClick={() => setSelectedAgent(agent)}
          >
            {agent}
          </Button>
        ))}
      </div>
    )
  }, [conversationDetail?.data, isLoadingConversationDetail, selectedAgent])

  return (
    <Modal.Root isOpen={isOpen} onHandleOpen={onHandleOpen} size="small">
      <Modal.Body className={styles['modal-body']}>
        <Text variant="title-large">{t('conversationTableForceComplete')}</Text>
        <Text>{t('conversationTableForceCompleteDescription')}</Text>

        {activateAgents}

        <div>
          <Label>{t('conversationTableForceCompleteResponseLabel')}</Label>
          <Input
            value={responseRegenerateConversation}
            isTextArea
            onChange={setResponseRegenerateConversation}
          />
        </div>

        <Button
          onClick={() => onRequestRegenerateConversation()}
          disabled={isPendingRegenerateConversation}
        >
          {t('conversationTableForceCompleteButton')}
        </Button>

        {isSuperUser(whoAmI?.email) && (
          <Button
            variant="secondary"
            onClick={() => onRequestCreateWhatsappMessage()}
            disabled={
              isPendingCreateWhatsappMessage || !responseRegenerateConversation
            }
          >
            {t('conversationTableCreateWhatsappMessageButton')}
          </Button>
        )}
      </Modal.Body>
    </Modal.Root>
  )
}
